/**
 * VetrinaAgenteSection — sezione landing "Il tuo sito personale" (PRO).
 *
 * Spiega in modo concreto cos'è la Vetrina Agente: un mini-sito al nome
 * dell'agente con i suoi immobili attivi, da mettere ovunque (bio Instagram,
 * firma email, biglietti da visita). Mostra le 4 estetiche disponibili
 * come piccole "card mock" per dare il senso visivo dei layout.
 *
 * iter v32 r37 · User M1428.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, At, Globe, Buildings, Eye, SealCheck, Lock, House,
  WhatsappLogo, IdentificationCard,
} from "@phosphor-icons/react";

const STYLES = [
  {
    id: "elegante",
    name: "Elegante",
    bg: "#F1F4ED",
    fg: "#1C1C1A",
    font: "'Bodoni Moda', serif",
    italic: true,
    weight: 400,
    upper: false,
    desc: "Magazine cover · Bodoni",
  },
  {
    id: "luxury",
    name: "Lussuoso",
    bg: "#F5F0E6",
    fg: "#1C1C1A",
    font: "'Spectral', serif",
    italic: true,
    weight: 200,
    upper: false,
    desc: "Spectral fine · centrato",
  },
  {
    id: "moderno",
    name: "Moderno",
    bg: "#1C1C1A",
    fg: "#FAFAF7",
    font: "'Archivo', sans-serif",
    italic: false,
    weight: 900,
    upper: true,
    desc: "Statement · Archivo Bold",
  },
  {
    id: "minimal",
    name: "Minimal",
    bg: "#FAFAF7",
    fg: "#1C1C1A",
    font: "'Space Grotesk', sans-serif",
    italic: false,
    weight: 400,
    upper: false,
    desc: "Manifesto svizzero",
  },
];

const USAGES = [
  { Icon: At,                 label: "Bio Instagram",           sub: "Un solo link, sempre aggiornato" },
  { Icon: WhatsappLogo,       label: "Stato WhatsApp Business", sub: "Lo apri direttamente dal contatto" },
  { Icon: IdentificationCard, label: "Biglietti da visita",     sub: "Affianca al QR · tutto in una pagina" },
  { Icon: Globe,              label: "Firma email",             sub: "Sotto al numero, sempre visibile" },
];

export default function VetrinaAgenteSection() {
  const [active, setActive] = useState("elegante");
  const cur = STYLES.find((s) => s.id === active) || STYLES[0];

  return (
    <section
      id="vetrina-agente"
      className="bg-[#FAFAF7] py-16 sm:py-24 lg:py-32 px-5 sm:px-8 lg:px-12 border-t border-[#E5E1D8]"
      data-testid="landing-vetrina-agente"
    >
      <div className="max-w-6xl mx-auto">
        {/* Eyebrow forte "Unico in Italia" iter v32 r37u · User M1453 */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="vetrina-eyebrow">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1C1C1A] text-white text-[10px] uppercase tracking-[0.18em] font-semibold">
            <SealCheck size={11} weight="fill" />
            Unico in Italia
          </div>
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#7C8B6E] inline-flex items-center gap-1.5">
            <Lock size={12} weight="fill" />
            Incluso nel Pro
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* COL LEFT — copy */}
          <div className="lg:col-span-5">
            <h2
              className="font-editorial text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.02] tracking-tight text-[#1C1C1A] mb-5"
              data-testid="vetrina-title"
            >
              Il primo sito personale{" "}
              <span className="italic">per agenti immobiliari.</span>
            </h2>
            <p className="text-[16px] sm:text-[17px] leading-relaxed text-[#3F3E3A] mb-6 max-w-[40ch]">
              <strong>prontoimmo.it/agente/<span className="italic text-[#7C8B6E]">il-tuo-nome</span></strong> ·
              biografia, contatti e tutti i tuoi immobili attivi, sempre aggiornati in
              automatico. Quattro estetiche editoriali, colore personalizzato. Non è un profilo dentro un portale: è davvero <strong>il tuo dominio</strong>.
            </p>

            {/* Usages */}
            <ul className="space-y-3 mb-8" data-testid="vetrina-usages">
              {USAGES.map(({ Icon, label, sub }) => (
                <li key={label} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md border border-[#E5E1D8] flex items-center justify-center flex-shrink-0">
                    <Icon size={16} weight="regular" className="text-[#7C8B6E]" />
                  </div>
                  <div>
                    <div className="text-[14.5px] font-medium text-[#1C1C1A]">{label}</div>
                    <div className="text-[12.5px] text-[#6B6A65]">{sub}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="vetrina-cta-register"
              >
                Inizia gratis <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                to="/pricing"
                className="btn-outline inline-flex items-center gap-2"
                data-testid="vetrina-cta-pricing"
              >
                Vedi il piano Pro
              </Link>
            </div>
            <p className="text-[12px] text-[#6B6A65] mt-3">Inclusa nel piano Pro · cambi stile e colore quando vuoi.</p>
          </div>

          {/* COL RIGHT — interactive preview */}
          <div className="lg:col-span-7">
            {/* Style chooser */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4" data-testid="vetrina-style-tabs">
              {STYLES.map((s) => {
                const a = s.id === active;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActive(s.id)}
                    className={`text-left rounded-lg border p-2.5 transition-all ${
                      a ? "border-[#1C1C1A] ring-2 ring-[#1C1C1A]/15 bg-white" : "border-[#E5E1D8] bg-white hover:border-[#9DA797]"
                    }`}
                    data-testid={`vetrina-style-tab-${s.id}`}
                  >
                    <div
                      className="w-full h-7 rounded-sm flex items-center justify-center mb-1.5"
                      style={{ background: s.bg, border: "1px solid rgba(28,28,26,0.12)" }}
                    >
                      <span
                        style={{
                          fontFamily: s.font,
                          color: s.fg,
                          fontStyle: s.italic ? "italic" : "normal",
                          fontWeight: s.weight,
                          textTransform: s.upper ? "uppercase" : "none",
                          fontSize: 14,
                          lineHeight: 1,
                          letterSpacing: s.upper ? "-0.02em" : "0",
                        }}
                      >
                        Aa
                      </span>
                    </div>
                    <div className="text-[11px] sm:text-[12px] font-medium text-[#1C1C1A] leading-tight">{s.name}</div>
                  </button>
                );
              })}
            </div>

            {/* Big mock — replica visiva semplificata della pagina /agente/:slug */}
            <div
              className="rounded-2xl overflow-hidden shadow-xl border"
              style={{ background: cur.bg, borderColor: "rgba(28,28,26,0.12)" }}
              data-testid="vetrina-mock-card"
            >
              {/* URL bar finto */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: cur.fg === "#FAFAF7" ? "rgba(255,255,255,0.12)" : "rgba(28,28,26,0.08)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: cur.fg, opacity: 0.4 }} />
                <span className="w-2 h-2 rounded-full" style={{ background: cur.fg, opacity: 0.25 }} />
                <span className="w-2 h-2 rounded-full" style={{ background: cur.fg, opacity: 0.15 }} />
                <div className="flex-1 ml-2 text-[11px] font-mono truncate" style={{ color: cur.fg, opacity: 0.6 }}>
                  prontoimmo.it/agente/<span style={{ fontStyle: "italic" }}>mario-rossi</span>
                </div>
                <SealCheck size={12} weight="fill" style={{ color: cur.fg, opacity: 0.7 }} />
              </div>
              {/* Hero */}
              <div className="p-6 sm:p-10 lg:p-14">
                <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: cur.fg, opacity: 0.55 }}>
                  Agente immobiliare · Immobili curati
                </div>
                <div
                  className="leading-[0.96] mb-4 text-[36px] sm:text-[54px] lg:text-[70px]"
                  style={{
                    fontFamily: cur.font, color: cur.fg, fontStyle: cur.italic ? "italic" : "normal",
                    fontWeight: cur.weight, textTransform: cur.upper ? "uppercase" : "none",
                    letterSpacing: cur.upper ? "-0.02em" : "0",
                  }}
                >
                  Luca Ferrari
                </div>
                <p className="text-[13.5px] sm:text-[15px] leading-relaxed mb-5 max-w-md" style={{ color: cur.fg, opacity: 0.78 }}>
                  «Mi occupo di vendita e acquisto immobili a Milano da 12 anni.
                  Ascolto prima di proporre.»
                </p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2.5 py-1 rounded-sm border" style={{ color: cur.fg, borderColor: cur.fg === "#FAFAF7" ? "rgba(255,255,255,0.25)" : "rgba(28,28,26,0.20)" }}>
                    +39 348 12 34 567
                  </span>
                  <span className="px-2.5 py-1 rounded-sm border" style={{ color: cur.fg, borderColor: cur.fg === "#FAFAF7" ? "rgba(255,255,255,0.25)" : "rgba(28,28,26,0.20)" }}>
                    WhatsApp
                  </span>
                  <span className="px-2.5 py-1 rounded-sm border" style={{ color: cur.fg, borderColor: cur.fg === "#FAFAF7" ? "rgba(255,255,255,0.25)" : "rgba(28,28,26,0.20)" }}>
                    mario.rossi@…
                  </span>
                </div>
              </div>
              {/* Properties row */}
              <div className="px-6 sm:px-10 lg:px-14 pb-8 sm:pb-12">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 h-px" style={{ background: cur.fg, opacity: 0.18 }} />
                  <span className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.42em]" style={{ color: cur.fg, opacity: 0.55 }}>
                    Portfolio · 03 immobili
                  </span>
                  <div className="flex-1 h-px" style={{ background: cur.fg, opacity: 0.18 }} />
                </div>
                <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
                  {[
                    { title: "Trilocale", city: "Brera",        sqm: 95,  price: 745 },
                    { title: "Loft",      city: "Isola",        sqm: 78,  price: 520 },
                    { title: "Villa",     city: "Lago di Como", sqm: 220, price: 1200 },
                  ].map((p, i) => (
                    <PropertyTile key={i} p={p} cur={cur} />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between text-[11.5px] text-[#6B6A65] gap-3">
              <span className="flex items-center gap-1.5">
                <Eye size={12} weight="regular" /> Anteprima reale del layout «{cur.name}»
              </span>
              <span className="hidden sm:flex items-center gap-1.5">
                <Buildings size={12} weight="regular" /> Aggiornata ad ogni nuovo annuncio
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PropertyTile({ p, cur }) {
  const dark = cur.fg === "#FAFAF7";
  return (
    <div
      className="rounded-sm overflow-hidden flex flex-col"
      style={{
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(28,28,26,0.04)",
        border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(28,28,26,0.10)"}`,
      }}
    >
      <div className="aspect-[5/4] flex items-center justify-center" style={{ background: dark ? "rgba(255,255,255,0.08)" : "rgba(28,28,26,0.06)" }}>
        <House size={20} weight="light" style={{ color: cur.fg, opacity: 0.4 }} />
      </div>
      <div className="p-2 sm:p-2.5">
        <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.22em] mb-0.5" style={{ color: cur.fg, opacity: 0.55 }}>
          {p.city}
        </div>
        <div
          className="text-[12px] sm:text-[14px] leading-tight mb-1"
          style={{
            fontFamily: cur.font, color: cur.fg, fontStyle: cur.italic ? "italic" : "normal",
            fontWeight: Math.min(cur.weight, 700),
            textTransform: cur.upper ? "uppercase" : "none",
          }}
        >
          {p.title}
        </div>
        <div className="text-[10.5px] sm:text-[11.5px] font-mono" style={{ color: cur.fg, opacity: 0.75 }}>
          {p.sqm} m² · € {p.price}k
        </div>
      </div>
    </div>
  );
}
